import { useRouter } from 'next/router';
import { useState } from 'react';
import { useTracking } from '@packages/tracking';
import { remotePromotion } from '@packages/cms-components/src/atoms';
import { useAtom } from 'jotai';
import { useCookies } from '@packages/shared';
import type { Promotion, PromotionVariantsData } from './types';
import { PromotionAsAccordion, PromotionAsChoiceLayer, PromotionAsLink } from './variants';

/**
 * The Promotionbanner-Content-Wrapper renders the corresponding variant by the delivered data
 *
 * @param promotionData Object of shop, active and/or selected promotion data
 * - shop: promotionData by shopId e.g. storefront, damenmode, ...
 * - active: promotionData by currently user-active Bonuscode (read from session/cookie)
 * - selected: promotionData by user-selected Bonuscode - this can be query-Params ?BonusCode or ?P1 (base64 encoded bonuscode)
 * @returns JSX.Element to be used in PromotionBanner-Component
 */
export const PromotionBannerContentWrapper = ({
  promotionData: { shop, active, selected },
}: {
  promotionData: PromotionVariantsData;
}) => {
  const dispatchGTMEvent = useTracking();
  const { locale = 'de' } = useRouter();
  const [activeData, setActiveData] = useState<Promotion | undefined>(
    !active?.isExpired ? active : undefined,
  );
  const [selectedQueryData, setSelectedQueryData] = useState<Promotion | undefined>(
    !selected?.isExpired ? selected : undefined,
  );
  const [selectedRemoteData, setSelectedRemoteData] = useAtom(remotePromotion);
  const selectedData = (!selectedRemoteData?.isExpired && selectedRemoteData) || selectedQueryData;
  const promoDataForRender = (selectedData || activeData || shop) as Promotion;
  const [isActive, setIsActive] = useState<boolean>(
    promoDataForRender?.textsCode === activeData?.textsCode,
  );
  const [showAccordionOpen, setShowAccordionOpen] = useState<boolean>(false);
  const { getCookies, destroyCookie, setCookie } = useCookies();
  if (!promoDataForRender) {
    return null;
  }
  const { bonuscode: currentCodeByCookie } = getCookies();

  /* the choiceLayer and the accordion promotion can change the active code
   - this code has to be registered as active and the UI must respond to the changed information */
  const setActiveCode = async (
    cartCodeToActivate: string | null,
    textsCodeToActivate: string | null,
  ) => {
    const { apiKeyManager } = await import('@empiriecom/mybuy-session');
    // NJS-3302 await the user session - a new session cleanes up local storage and would corrupt local storage if this runs after setBonuscodes
    await apiKeyManager.getApiKey();
    // NJS-1214 - cleanup all bonuscodes by override before adding the new one
    if (cartCodeToActivate) {
      await apiKeyManager.setBonuscodes([cartCodeToActivate], true);
    } else {
      const currentCodes = apiKeyManager.getBonuscodes();
      if (currentCodes.length > 0) {
        await apiKeyManager.removeBonuscodes(currentCodes);
        // remove bonuscode cookie here - the apikeymanager not yet does it - may be removed when NJS-1249 is resolved
        destroyCookie(null, 'bonuscode');
      }
    }
    destroyCookie(null, 'textsBonusCode');
    if (textsCodeToActivate) {
      setCookie(null, 'textsBonusCode', textsCodeToActivate);
    }
    // INSPIRE-3358 - either selectedData oder shopData may be redeemed - it depends on the code to activate
    const dataToRedeem = [selectedData, shop].find(
      (promotionData) => promotionData && promotionData.cartCode === cartCodeToActivate,
    );
    if (dataToRedeem?.pro?.[0]) {
      const { checkoutManager } = await import('@empiriecom/mybuy-checkout');
      checkoutManager
        .addToBasket(dataToRedeem.pro[0], 1, false, {}, [], {}, locale)
        .catch(() => {});
    }
    if (dataToRedeem) {
      setActiveData(dataToRedeem);
      setShowAccordionOpen(true);
    } else {
      setShowAccordionOpen(false);
    }
    setSelectedQueryData(undefined);
    setSelectedRemoteData(null);
    if (cartCodeToActivate) {
      setIsActive(true);
    }
  };

  // NJS-1253 get rid of active expired code
  if (
    !selectedData &&
    promoDataForRender &&
    'cartCode' in promoDataForRender &&
    currentCodeByCookie !== promoDataForRender.cartCode
  ) {
    setActiveCode(null, null);
  }

  // ad hoc activate the valid selected code (by remote promotion or query param) if there is currently no active code in cart
  if (
    !activeData &&
    selectedData?.textsCode &&
    selectedData?.cartCode &&
    promoDataForRender &&
    'cartCode' in promoDataForRender &&
    promoDataForRender.cartCode
  ) {
    setActiveCode(promoDataForRender.cartCode, promoDataForRender.textsCode);
    if ('feedbackTitle' in promoDataForRender) {
      promoDataForRender.title = promoDataForRender.feedbackTitle || promoDataForRender.title;
    }
  }

  const { promotionAsLink, linkReady, title, name } = promoDataForRender;

  /**
   * DISPLAYING THE PROMOTION AS ...
   */

  /* ... CHOICE-LAYER when there was already an activated code
   in cart but additionally there is a selected code by query-params */
  if (
    // check isActive dependent on remote data - query-data is serverside-available while remote date is added clientside
    ((selectedQueryData?.textsCode && !isActive) ||
      (!selectedRemoteData?.isExpired && selectedRemoteData?.textsCode && isActive)) &&
    selectedData?.textsCode &&
    activeData?.textsCode &&
    selectedData.textsCode !== activeData.textsCode
  ) {
    dispatchGTMEvent({
      event: 'trackPromoBanner',
      eventLabel: 'promo-banner',
      eventValue: `choice_layer_open_${name}`,
    });

    return (
      <PromotionAsChoiceLayer
        activePromotion={activeData}
        selectedPromotion={selectedData}
        setActiveCode={setActiveCode}
        key="promotionAsChoiceLayer"
      />
    );
  }

  dispatchGTMEvent({
    event: 'trackPromoBanner',
    eventLabel: 'promo-banner',
    eventValue: `promobanner_displayed_${name}`,
  });

  /* ... LINK when the promotion-data serve a link */
  if (promotionAsLink && linkReady) {
    return <PromotionAsLink linkReady={linkReady} title={title} key="promotionAsLink" />;
  }

  /* ... ACCORDION as default */
  return (
    <PromotionAsAccordion
      {...promoDataForRender}
      isActive={isActive}
      setActivePromotionCode={setActiveCode}
      // the changed key for ad-hoc-activation is needed to reset the isExpanded state in the accordion
      key={`promotionAsAccordion-${showAccordionOpen}`}
      defaultExpanded={showAccordionOpen}
    />
  );
};
